import {IonIcon} from "@ionic/react";
import {closeCircle} from "ionicons/icons";
import {styled} from "styled-components";
import type {UnifiedAttachmentData} from "app/components/hooks/useAttachmentCollectionEditor";
import {FILE_STATES} from "@rpldy/uploady";
import documentPlaceholder from "app/employee/att/document-placeholder.png";

const Strip = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  overflow-x: auto;
  overflow-y: visible;
  padding: 0.5rem 0.75rem 0 0;
`;

const ThumbnailWrapper = styled.div`
  position: relative;
  flex-shrink: 0;
  border-radius: 0.5rem;
  border: 1px solid var(--fst-border-color);
`;

const Thumbnail = styled.div`
  width: 3rem;
  height: 3rem;
  overflow: hidden;
  position: relative;
`;

const ThumbnailImg = styled.img`
  border-radius: 0.5rem;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const RemoveIcon = styled(IonIcon)`
  font-size: 1.25rem;
  background: white;
  border-radius: 50%;
  padding: 0;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: -0.6rem;
  right: -0.6rem;
  width: 1.5rem;
  height: 1.5rem;
  margin: 0;
  padding: 0;
  background: transparent;
  display: block;
  z-index: 1;
  color: var(--ion-color-danger);
`;

const DimOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.5);
  transition: bottom 0.3s ease;
  pointer-events: none;
`;

const ProgressLabel = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: 700;
  color: white;
  text-shadow:
    1px 1px 0 #000,
    -1px 1px 0 #000,
    1px -1px 0 #000,
    -1px -1px 0 #000;
  pointer-events: none;
`;

interface Props {
  unifiedAttachments: UnifiedAttachmentData[];
}

const isUploading = (state: FILE_STATES) =>
  state === FILE_STATES.ADDED || state === FILE_STATES.PENDING || state === FILE_STATES.UPLOADING;

export const CompactAttachmentStrip = ({unifiedAttachments}: Props) => (
  <Strip>
    {unifiedAttachments.map((att) => {
      const uploading = isUploading(att.state);
      const completedPercent = att.completed * 100;
      return (
        <ThumbnailWrapper key={att.key}>
          <Thumbnail>
            <ThumbnailImg src={att.thumbnailUrl ?? att.fullSizeUrl ?? documentPlaceholder} />
            {uploading && (
              <>
                {/* inline style: a styled-component prop would generate a new class per percent tick */}
                <DimOverlay style={{bottom: `${completedPercent}%`}} />
                <ProgressLabel>{Math.round(completedPercent)}%</ProgressLabel>
              </>
            )}
          </Thumbnail>
          <RemoveButton onClick={att.handleRemove}>
            <RemoveIcon icon={closeCircle} />
          </RemoveButton>
        </ThumbnailWrapper>
      );
    })}
  </Strip>
);
